import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, permissions: Object, currentUserId: Number }

  connect() {
    this.modal = new bootstrap.Modal(this.element)
    this.form = document.getElementById("sharedWatchForm")
    this.saveBtn = document.getElementById("sharedWatchSaveBtn")
    this.episodeNameEl = document.getElementById("sharedWatchEpisodeName")
    this.episodeBadgeEl = document.getElementById("sharedWatchEpisodeBadge")
    this.episodeSlugInput = document.getElementById("sharedWatchEpisodeSlug")
    this.contentSlugInput = document.getElementById("sharedWatchContentSlug")

    this.boundOpen = this.handleOpen.bind(this)
    document.addEventListener("shared-watch:open", this.boundOpen)

    this.setupStarRatings()
    this.setupFormSubmit()
  }

  disconnect() {
    document.removeEventListener("shared-watch:open", this.boundOpen)
  }

  handleOpen(event) {
    const { episodeSlug, contentSlug, contentName, seasonNumber, episodeNumber, episodeName, tagIds } = event.detail

    this.episodeSlugInput.value = episodeSlug || ""
    this.contentSlugInput.value = contentSlug || ""

    document.getElementById("sharedWatchModalLabel").textContent = "Watch with Friends"

    if (episodeName) {
      this.episodeNameEl.textContent = episodeName
      const s = String(seasonNumber).padStart(2, "0")
      const e = String(episodeNumber).padStart(2, "0")
      this.episodeBadgeEl.innerHTML =
        `<i class="bi bi-collection-play me-1"></i>S${s} E${e}`
    } else {
      this.episodeNameEl.textContent = ""
      this.episodeBadgeEl.innerHTML = ""
    }

    this.resetForm()
    this.#applyTagPermissions(tagIds || [])

    if (episodeSlug) {
      fetch(`/episode/${episodeSlug}/participant-data`, {
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
        .then(response => response.json())
        .then(data => { this.prePopulateParticipants(data) })
        .catch(() => {})
        .finally(() => { this.modal.show() })
    } else {
      this.modal.show()
    }
  }

  prePopulateParticipants(data) {
    Object.entries(data).forEach(([userId, episodeData]) => {
      if (episodeData.watched) {
        const watchedCheckbox = document.getElementById(`sharedWatched_${userId}`)
        if (watchedCheckbox) watchedCheckbox.checked = true
      }

      const rating = episodeData.rating
      if (!rating) return

      const starContainer = document.getElementById(`sharedStarRating_${userId}`)
      if (!starContainer) return

      const normalizedRating = rating % 1 === 0 ? Math.floor(rating).toString() : rating.toString()
      const ratingStr = normalizedRating.replace(".", "-")
      const ratingRadio = document.getElementById(`shared-rating-${userId}-${ratingStr}`)
      if (!ratingRadio) return

      ratingRadio.checked = true
      starContainer.classList.add("rating-active")
      starContainer.querySelectorAll(".star-container").forEach((container, index) => {
        const starNumber = index + 1
        container.classList.remove("highlighted", "half-highlighted")
        if (rating >= starNumber) {
          container.classList.add("highlighted")
        } else if (rating >= starNumber - 0.5) {
          container.classList.add("half-highlighted")
        }
      })
    })
  }

  resetForm() {
    this.element.querySelectorAll("input[data-shared-watch-watched]").forEach(cb => {
      cb.checked = false
    })
    this.element.querySelectorAll(".shared-star-rating").forEach(container => {
      container.querySelectorAll("input[type='radio']").forEach(r => { r.checked = false })
      container.classList.remove("rating-active")
      container.querySelectorAll(".star-container").forEach(c => {
        c.classList.remove("highlighted", "half-highlighted")
      })
    })
    this.element.querySelectorAll("input[type='radio'][value='']").forEach(r => {
      r.checked = true
    })
    this.element.querySelectorAll("[data-user-id]:not([data-current-user])").forEach(block => {
      block.classList.remove("d-none")
      const watchedSection = block.querySelector("[data-watched-section]")
      const ratingSection = block.querySelector("[data-rating-section]")
      const noPermMsg = block.querySelector("[data-no-perm-msg]")
      if (watchedSection) watchedSection.classList.remove("d-none")
      if (ratingSection) ratingSection.classList.remove("d-none")
      if (noPermMsg) noPermMsg.classList.add("d-none")
    })
  }

  #applyTagPermissions(tagIds) {
    const tagIdSet = new Set(tagIds.map(String))
    const permissions = this.permissionsValue || {}

    this.element.querySelectorAll("[data-user-id]").forEach(block => {
      if (block.hasAttribute("data-current-user")) return

      const userId = block.dataset.userId
      const friendPerms = permissions[userId]

      if (!friendPerms || Object.keys(friendPerms).length === 0) {
        block.classList.add("d-none")
        return
      }

      const matchingEntries = Object.entries(friendPerms).filter(([tagId]) =>
        tagIdSet.size === 0 || tagIdSet.has(tagId)
      )

      if (matchingEntries.length === 0) {
        block.classList.add("d-none")
        return
      }

      let allowWatch = false
      let allowRate = false

      matchingEntries.forEach(([, perm]) => {
        if (perm.watch) allowWatch = true
        if (perm.rate) allowRate = true
      })

      const watchedSection = block.querySelector("[data-watched-section]")
      const ratingSection = block.querySelector("[data-rating-section]")
      const noPermMsg = block.querySelector("[data-no-perm-msg]")

      if (watchedSection) watchedSection.classList.toggle("d-none", !allowWatch)
      if (ratingSection) ratingSection.classList.toggle("d-none", !allowRate)
      if (noPermMsg) noPermMsg.classList.toggle("d-none", allowWatch || allowRate)
    })
  }

  setupStarRatings() {
    this.element.querySelectorAll(".shared-star-rating").forEach(container => {
      const starContainers = container.querySelectorAll(".star-container")
      const watchedId = container.dataset.friendWatchedId
      const watchedCheckbox = watchedId ? document.getElementById(watchedId) : null

      const updateDisplay = (value) => {
        container.classList.add("rating-active")
        starContainers.forEach((sc, index) => {
          const starNumber = index + 1
          sc.classList.remove("highlighted", "half-highlighted")
          if (value >= starNumber) {
            sc.classList.add("highlighted")
          } else if (value >= starNumber - 0.5) {
            sc.classList.add("half-highlighted")
          }
        })
      }

      const clearDisplay = () => {
        container.classList.remove("rating-active")
        starContainers.forEach(sc => sc.classList.remove("highlighted", "half-highlighted"))
      }

      container.querySelectorAll("input[type='radio']").forEach(radio => {
        radio.addEventListener("change", () => {
          if (radio.checked && radio.value) {
            updateDisplay(parseFloat(radio.value))
            if (watchedCheckbox && !watchedCheckbox.checked) {
              watchedCheckbox.checked = true
            }
          }
        })
      })

      starContainers.forEach(sc => {
        const halfLabel = sc.querySelector(".star-half-label")
        const fullLabel = sc.querySelector(".star-full-label")

        sc.addEventListener("mouseenter", () => {
          if (fullLabel) updateDisplay(parseFloat(fullLabel.dataset.value))
        })
        if (halfLabel) {
          halfLabel.addEventListener("mouseenter", (e) => {
            e.stopPropagation()
            updateDisplay(parseFloat(halfLabel.dataset.value))
          })
        }
        if (fullLabel) {
          fullLabel.addEventListener("mouseenter", (e) => {
            e.stopPropagation()
            updateDisplay(parseFloat(fullLabel.dataset.value))
          })
        }
      })

      container.addEventListener("mouseleave", () => {
        const checked = container.querySelector("input[type='radio']:checked")
        if (checked && checked.value) {
          updateDisplay(parseFloat(checked.value))
        } else {
          clearDisplay()
        }
      })

      const clearBtn = container.querySelector("[data-clear-rating]")
      if (clearBtn) {
        clearBtn.addEventListener("click", (e) => {
          e.preventDefault()
          container.querySelectorAll("input[type='radio']").forEach((r) => { r.checked = false })
          const noneRadio = container.closest(".rating-input")?.querySelector("input[value='']")
          if (noneRadio) noneRadio.checked = true
          clearDisplay()
        })
      }
    })
  }

  setupFormSubmit() {
    this.form.addEventListener("submit", (e) => {
      e.preventDefault()
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content || ""
      const formData = new FormData(this.form)

      this.saveBtn.disabled = true
      this.saveBtn.textContent = "Saving..."

      fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: formData,
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            this.modal.hide()
            document.dispatchEvent(new CustomEvent("shared-watch:completed", { detail: data }))
          }
        })
        .catch(error => console.error("Error:", error))
        .finally(() => {
          this.saveBtn.disabled = false
          this.saveBtn.innerHTML = '<i class="bi bi-floppy"></i> Save'
        })
    })
  }
}
